/**
 * Confetti with LS colors and slower, floatier presets.
 *
 * To be used with CelebrationWrapper.
 */
import * as React from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";

import Confetti from "./confetti.tsx";
import {defaultShapes, shapesList} from "./ls-confetti-shapes.ts";

interface Props {
    /**
     * Width, in px, of a relative-positioned parent.
     *
     * Celebrations can fill the entire parent. Anything rendered outside the
     * parent will be clipped.
     */
    width: number;

    /**
     * Height, in px, of a relative-positioned parent.
     *
     * Celebrations can fill the entire parent. Anything rendered outside the
     * parent will be clipped.
     */
    height: number;

    /**
     * Celebrations should call this when the animation is complete to
     * indicate that the parent should unmount.
     */
    onAnimationComplete: () => void;

    /**
     * A multiplier which can be used to intensify the celebration based on
     * level achieved.
     */
    levelFactor?: number;

    /**
     * Passing in the most recently completed level, the first 11 levels all have
     * a unique celebration.
     */
    latestLevel?: number;
}

/**
 * Custom confetti component used for LearnStorm celebrations.
 */
const LSConfetti = (props: Props): React.ReactNode => {
    const confetti = React.useRef<React.ElementRef<typeof Confetti>>(null);

    useOnMountEffect(() => {
        if (confetti.current) {
            confetti.current.startConfetti(props.width / 2, -30);
        }
    });

    useTimeout(() => {
        props.onAnimationComplete();
    }, 7000);

    // default levelFactor to 1
    const levelFactor = props.levelFactor || 1;
    const latestLevel = props.latestLevel;

    let shapesTheme = defaultShapes;
    if (latestLevel && latestLevel <= 11) {
        shapesTheme = shapesList[latestLevel - 1];
    } else if (latestLevel && latestLevel > 11) {
        shapesTheme = shapesList[Math.floor(Math.random() * shapesList.length)];
    }

    return (
        <Confetti
            ref={confetti}
            width={props.width}
            height={props.height}
            gravity={2000 * levelFactor}
            numParticles={props.width * levelFactor * 0.1}
            spreadHorizontally={true}
            emitDuration={5000}
            shapes={shapesTheme}
            spin={10}
            twist={0}
            minSpeed={100}
            maxSpeed={200}
            minScale={1}
            maxScale={1}
        />
    );
};

export default LSConfetti;
